// FLAGS: authorization, personal-data, irreversible, silent-failure
// The client table shared by AllClientsView and MyClientsView, plus the account row actions:
// start chat, unlock, anonymize and remove second factor. Which of those appear is decided by props.
import {
  Box,
  Grid,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { ReactComponent as AnonymizeClientIcon } from "@src/resources/icons/anonymize-client.svg";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AddClientButton from "@src/components/AddClientButton";
import NothingFound from "@src/components/NothingFound";
import SearchBox from "@src/components/SearchBox";
import { pageSize } from "@src/constants";
import { env } from "@src/env";
import { isEmptyQueryResult } from "@src/queries/utils";
import { createChat } from "@src/services/chat.service";

import { useDebouncedCallback } from "use-debounce";
import {
  useClientAnonymizeMutation,
  useClientUnlockMutation,
  useRemove2FAMutation,
} from "@src/queries/clients";
import { useState } from "react";
import { QueryObserverResult } from "@tanstack/react-query";
import {
  ResponsePsychologistClientResponse,
  ResponsePsychologistClientResponsePaginated,
} from "@src/api/v1/data-contracts";
import ConfirmEmailDialog from "@src/components/ConfirmEmailDialog";

const tableCellStyle = "py-2 px-0 sub1";

export interface ClientsListProps {
  data: ResponsePsychologistClientResponsePaginated | undefined;
  isFetching: boolean;
  refetchData: () => Promise<
    QueryObserverResult<ResponsePsychologistClientResponsePaginated, Error>
  >;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  columns: { id: string; minWidth: number; maxWidth?: number }[];
  snackbarShowMessage: (message: string, severity: "success" | "error") => void;
  showClientDetails?: boolean;
  ableToInitiateChat?: boolean;
  ableToAddClient?: boolean;
  ableToAnonymizeClient?: boolean;
  ableToUnlockUser?: boolean;
  ableToRemove2FA?: boolean;
  showActionsColumn?: boolean;
}

const ClientsList: React.FC<ClientsListProps> = ({
  data,
  isFetching,
  refetchData,
  page,
  setPage,
  setSearch,
  columns,
  snackbarShowMessage,
  showClientDetails = true,
  ableToInitiateChat = true,
  ableToAddClient = true,
  ableToAnonymizeClient = false,
  ableToUnlockUser = false,
  ableToRemove2FA = false,
  showActionsColumn = false,
}) => {
  const navigate = useNavigate();
  const { mutateAsync: unlockMutation } = useClientUnlockMutation();
  const { mutateAsync: remove2FA } = useRemove2FAMutation();
  const { mutateAsync: anonymizeClient } = useClientAnonymizeMutation();

  const { t } = useTranslation();
  const [isAnonymizeClientDialogOpen, setIsAnonymizeClientDialogOpen] =
    useState(false);
  const [isRemove2FADialogOpen, setIsRemove2FADialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] =
    useState<ResponsePsychologistClientResponse>();
  const [openTableMenus, setOpenTableMenus] = useState<{
    [id: string]: HTMLElement | null;
  }>({});

  const copyEmailToClipboard = async (
    email: string,
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(email);
    snackbarShowMessage(t("Common.EmailClipboard"), "success");
  };

  const onSearchChange = useDebouncedCallback((searchValue: string) => {
    setSearch(searchValue);
    setPage(0);
  }, 500);

  const handleClickClientRow = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    client: ResponsePsychologistClientResponse,
  ) => {
    event.stopPropagation();
    if (showClientDetails) {
      navigate(`/clients/${client.id}`);
    }
  };

  const handleActionsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    clientId: string,
  ) => {
    const tableMenus = { ...openTableMenus };
    tableMenus[clientId] = event.currentTarget;
    setOpenTableMenus(tableMenus);
  };
  const handleActionsClose = (clientId: string) => {
    const tableMenus = { ...openTableMenus };
    tableMenus[clientId] = null;
    setOpenTableMenus(tableMenus);
  };

  const handleClickStartChat = async (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    client: ResponsePsychologistClientResponse,
  ) => {
    event.stopPropagation();

    try {
      await createChat(client);
      snackbarShowMessage(
        t("Clients.ClientChatInitiated", { userEmail: client.email }),
        "success",
      );
    } catch {
      snackbarShowMessage(
        t("Clients.ClientChatInitiateError", { userEmail: client.email }),
        "error",
      );
    } finally {
      handleActionsClose(client.id!);
    }
  };

  // FLAGS: silent-failure
  // The mutation is not awaited, so the `catch` can never run: a rejected unlock still shows the
  // success snackbar (itself an empty string) and the operator believes the account is open.
  const handleUnlockUser = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    clientId: string,
  ) => {
    event.stopPropagation();
    try {
      unlockMutation(clientId);
      snackbarShowMessage("", "success");
    } catch {
      snackbarShowMessage("", "error");
    } finally {
      handleActionsClose(clientId);
    }
  };

  const handleAnonymizeClientClicked = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    client: ResponsePsychologistClientResponse,
  ) => {
    event.stopPropagation();
    setSelectedClient(client);
    setIsAnonymizeClientDialogOpen(true);
    handleActionsClose(client.id!);
  };

  // FLAGS: irreversible, personal-data
  // Anonymisation cannot be undone — the endpoint's own summary says so. The only guard is
  // ConfirmEmailDialog making the operator retype the client's email; there is no server-side
  // second step, so a change that loosens that dialog removes the whole guard.
  const handleAnonymizeClientConfirmed = async (clientId: string) => {
    try {
      await anonymizeClient(clientId);
      snackbarShowMessage(t("AnonymizeClient.Message.Success"), "success");
      setIsAnonymizeClientDialogOpen(false);
      refetchData();
    } catch {
      snackbarShowMessage(t("Common.GenericErrorMessage"), "error");
    }
  };
  // FLAGS: authorization, identity
  // Strips the client's second factor, so their account is afterwards reachable with the password
  // alone until they re-enrol. Same retype-the-email dialog is the only client-side guard.
  const handleRemove2FAConfirmed = async (clientId: string) => {
    try {
      await remove2FA(clientId);
      snackbarShowMessage(t("Remove2FADialog.Message.Success"), "success");
      setIsRemove2FADialogOpen(false);
      refetchData();
    } catch {
      snackbarShowMessage(t("Common.GenericErrorMessage"), "error");
    }
  };

  const handleRemove2FAClicked = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    client: ResponsePsychologistClientResponse,
  ) => {
    event.stopPropagation();
    setSelectedClient(client);
    setIsRemove2FADialogOpen(true);
    handleActionsClose(client.id!);
  };

  if (data === undefined) return null;
  const clients = data.data;
  const total = data.pagination?.total ?? 0;
  const hasNextPage = data.pagination?.hasNextPage ?? false;

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box display="flex">
            <SearchBox
              onChange={onSearchChange}
              placeholder={t("Clients.List.SearchPlaceholder")}
              className="mr-3 w-auto flex-grow"
            />
            {ableToAddClient ? (
              <AddClientButton data-cy="add-client-button" />
            ) : null}
          </Box>
        </Grid>

        {isEmptyQueryResult(data) ? (
          <NothingFound translationKey="Clients.List.NotFound" />
        ) : (
          <Grid item xs={12} data-cy="clients-table">
            <Paper className="py-2 px-3 rounded-xl">
              <div className="flex items-center">
                <Icon className="mr-1">groups</Icon>
                <p className="h5">{t("Clients.Pagetitle")}</p>
                <TablePagination
                  component="div"
                  style={{
                    marginLeft: "auto",
                    marginRight: -8,
                  }}
                  count={total}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[10]}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  slotProps={{
                    actions: {
                      nextButton: {
                        disabled: isFetching || !hasNextPage,
                      },
                      previousButton: {
                        disabled: isFetching || page === 0,
                      },
                    },
                  }}
                />
              </div>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          style={{
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          }}
                          className={tableCellStyle}
                        >
                          {t(column.id)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients!.map((client) => (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={client.id}
                        classes={{
                          hover:
                            "hover:bg-blue-500 hover:bg-opacity-10 hover:rounded",
                          root: "opacity-70 cursor-pointer",
                        }}
                        onClick={(e) => handleClickClientRow(e, client)}
                      >
                        <TableCell className={tableCellStyle}>
                          OU-{client.userNumber}
                        </TableCell>
                        <TableCell className={tableCellStyle}>
                          {client.lastName}
                        </TableCell>
                        <TableCell className={tableCellStyle}>
                          {client.firstName}
                        </TableCell>
                        <TableCell className={tableCellStyle}>
                          <div className="flex sub1">
                            {client.email}

                            <FileCopyOutlinedIcon
                              className="ml-1 hover:text-blue-500"
                              onClick={(event) =>
                                copyEmailToClipboard(client.email!, event)
                              }
                            />
                          </div>
                        </TableCell>
                        {showActionsColumn && (
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            className="p-0 text-center"
                          >
                            <div className="flex justify-center items-center">
                              <IconButton
                                data-testid={`actions-${client.id}`}
                                onClick={(e) =>
                                  handleActionsClick(e, client.id!)
                                }
                                className="flex items-center p-1 hover:bg-blue-500 group-hover:opacity-70"
                              >
                                <MoreHorizIcon className="text-2xl text-indigo-800" />
                              </IconButton>
                            </div>
                            <Menu
                              key={client.id}
                              anchorEl={openTableMenus[client.id!]}
                              open={Boolean(openTableMenus[client.id!])}
                              onClose={() => handleActionsClose(client.id!)}
                              MenuListProps={{
                                "aria-labelledby": "basic-button",
                              }}
                              slotProps={{
                                paper: {
                                  className:
                                    "rounded border border-solid border-slate-300",
                                },
                              }}
                            >
                              {env.REACT_APP_CHAT_FEATURE_ENABLED &&
                                ableToInitiateChat && (
                                  <MenuItem
                                    onClick={(event) =>
                                      handleClickStartChat(event, client)
                                    }
                                    disabled={!ableToAddClient}
                                    data-testid={`initiate-chat-${client.id}`}
                                  >
                                    {t("Clients.ClientChatInitiate")}
                                  </MenuItem>
                                )}
                              {ableToUnlockUser && (
                                <MenuItem
                                  onClick={(event) =>
                                    handleUnlockUser(event, client.id!)
                                  }
                                  disabled={!client.isLockedOut}
                                  data-testid={`unlock-user-${client.id}`}
                                >
                                  {t("Clients.List.UnlockUser")}
                                </MenuItem>
                              )}
                              {ableToAnonymizeClient && (
                                <MenuItem
                                  onClick={(event) =>
                                    handleAnonymizeClientClicked(event, client)
                                  }
                                  data-testid={`anonymize-icon-${client.id}`}
                                >
                                  {t("Clients.List.AnonymizeClient")}
                                </MenuItem>
                              )}
                              {ableToRemove2FA && (
                                <MenuItem
                                  onClick={(event) =>
                                    handleRemove2FAClicked(event, client)
                                  }
                                  disabled={!client.twoFactorEnabled}
                                  data-testid={`remove-2fa-${client.id}`}
                                >
                                  {t("Clients.List.Remove2FA")}
                                </MenuItem>
                              )}
                            </Menu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
      {ableToAnonymizeClient && (
        <ConfirmEmailDialog
          open={isAnonymizeClientDialogOpen}
          client={selectedClient}
          onCancelled={() => {
            setIsAnonymizeClientDialogOpen(false);
          }}
          onConfirmed={() => {
            handleAnonymizeClientConfirmed(selectedClient!.id!);
          }}
          description={t("AnonymizeClient.Email.Description")}
          iconLigature={<AnonymizeClientIcon />}
          title={t("AnonymizeClient.Title")}
          data-cy="clients-list-anonymize-dialog"
        />
      )}
      {ableToRemove2FA && (
        <ConfirmEmailDialog
          open={isRemove2FADialogOpen}
          client={selectedClient}
          onCancelled={() => {
            setIsRemove2FADialogOpen(false);
          }}
          onConfirmed={() => {
            handleRemove2FAConfirmed(selectedClient!.id!);
          }}
          description={t("Remove2FADialog.Email.Description")}
          iconLigature="lock_reset"
          title={t("Remove2FADialog.Title")}
          data-cy="clients-list-anonymize-dialog"
        />
      )}
    </>
  );
};
export default ClientsList;
